import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, faFile, faFileImage, faFilePdf, 
  faFileWord, faFileExcel, faFilePowerpoint, faFileLines, 
  faFileZipper, faFileAudio, faFileVideo, faEye, faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import { useColorPalette } from '../hooks/useColorPalette';
import ImageLightbox from './ImageLightbox';

/**
 * Componente per visualizzare e gestire gli allegati con supporto lightbox per le immagini
 */
const AttachmentsViewer = ({ attachments, title = "Allegati", showPreview = true, downloadAllHandler, downloadInProgress }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const { colors = { primary: '#007bff', secondary: '#6c757d', success: '#28a745', accent: '#17a2b8', textOnAccent: '#ffffff', textOnPrimary: '#ffffff' } } = useColorPalette() || {};

  // Determina se il file è un'immagine
  const isImageFile = useCallback((file) => {
    if (!file) return false;
    
    // Se è un oggetto con url o urlFile
    if (typeof file === 'object' && (file.url || file.urlFile)) {
      const url = (file.url || file.urlFile).toLowerCase();
      // Estrai l'estensione dall'URL
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      const ext = fileName.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    }
    
    // Se è una stringa o altro formato
    if (typeof file === 'string' || (file && file.fileName || file.filename || file.name)) {
      const fileName = typeof file === 'string' ? file : (file.fileName || file.filename || file.name || '');
      const ext = fileName.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    }
    
    return false;
  }, []);

  // Ottiene l'icona in base al tipo di file
  const getFileTypeIcon = useCallback((file) => {
    if (!file) return faFile;
    
    let fileRef = '';
    
    // Se file è una stringa (URL), estraiamo il nome file
    if (typeof file === 'string') {
      const urlParts = file.split('/');
      fileRef = urlParts[urlParts.length - 1].split('?')[0] || '';
    } else {
      fileRef = file.fileName || file.filename || file.name || file.path || '';
      
      // Se abbiamo un url o urlFile, estraiamo il nome file da esso
      if (!fileRef && (file.url || file.urlFile)) {
        const urlParts = (file.url || file.urlFile).split('/');
        fileRef = urlParts[urlParts.length - 1].split('?')[0] || '';
      }
    }
    
    const ext = fileRef.split('.').pop().toLowerCase();
    
    const iconMap = {
      // Documenti
      'pdf': faFilePdf,
      'doc': faFileWord,
      'docx': faFileWord,
      'xls': faFileExcel,
      'xlsx': faFileExcel,
      'ppt': faFilePowerpoint,
      'pptx': faFilePowerpoint,
      'txt': faFileLines,
      // Immagini
      'jpg': faFileImage,
      'jpeg': faFileImage,
      'png': faFileImage,
      'gif': faFileImage,
      'bmp': faFileImage,
      'svg': faFileImage,
      'webp': faFileImage,
      // Archivi
      'zip': faFileZipper,
      'rar': faFileZipper,
      '7z': faFileZipper,
      'tar': faFileZipper,
      'gz': faFileZipper,
      // Audio/Video
      'mp3': faFileAudio,
      'wav': faFileAudio,
      'mp4': faFileVideo,
      'avi': faFileVideo,
      'mov': faFileVideo,
      'wmv': faFileVideo,
    };
    
    return iconMap[ext] || faFile;
  }, []);

  // Ottiene l'URL dell'immagine
  const getImageUrl = useCallback((file) => {
    // Se file è una stringa, è già un URL
    if (typeof file === 'string') {
      return file;
    }
    
    if (file.url) {
      return file.url;
    } else if (file.urlFile) {
      return file.urlFile;
    } else if (file.fileUrl) {
      return file.fileUrl;
    }
    return `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${file.path || file.filename || file.fileName || file.name}`;
  }, []);

  // Funzione per aprire il lightbox con le immagini
  const openLightbox = useCallback((files, startIndex = 0) => {
    // Filtra solo le immagini
    const imageFiles = files.filter(file => isImageFile(file));
    if (imageFiles.length === 0) return;
    
    // Ottiene gli URL delle immagini
    const imageUrls = imageFiles.map(file => getImageUrl(file));
    
    setLightboxImages(imageUrls);
    setCurrentImageIndex(startIndex);
    setLightboxOpen(true);
  }, [isImageFile, getImageUrl]);

  // Funzione per gestire il download di un file
  const handleDownload = useCallback((file, index) => {
    const fileKey = typeof file === 'string' ? `url_${index}` : (file._id || file.id || file.fileId || index);
    
    if (downloadingFiles[fileKey]) return;
    
    try {
      setDownloadingFiles(prev => ({
        ...prev,
        [fileKey]: true
      }));
      
      // URL del file
      const publicUrl = getImageUrl(file);
      
      // Estrae il nome del file dall'URL o usa quello fornito
      let fileName;
      if (typeof file === 'string') {
        const urlParts = file.split('/');
        fileName = decodeURIComponent(urlParts[urlParts.length - 1].split('?')[0]) || 'download';
      } else {
        // Prova prima con i campi nome, altrimenti estrai dall'URL
        fileName = file.fileName || file.filename || file.name;
        
        // Se non c'è nome ma c'è urlFile o url, estrai dall'URL
        if (!fileName && (file.urlFile || file.url)) {
          const urlParts = (file.urlFile || file.url).split('/');
          fileName = decodeURIComponent(urlParts[urlParts.length - 1].split('?')[0]);
        }
        
        // Fallback finale usando l'URL pubblico
        if (!fileName) {
          fileName = publicUrl.split('/').pop().split('?')[0] || 'download';
        }
      }
      
      // Crea link per download e simula click
      const link = document.createElement('a');
      link.href = publicUrl;
      link.download = fileName;
      link.target = '_blank'; // Apre in una nuova finestra se necessario
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Reimposta lo stato dopo un breve ritardo
      setTimeout(() => {
        setDownloadingFiles(prev => ({
          ...prev,
          [fileKey]: false
        }));
      }, 1000);
    } catch (error) {
      console.error('Errore durante il download del file:', error);
      setDownloadingFiles(prev => ({
        ...prev,
        [fileKey]: false
      }));
    }
  }, [downloadingFiles, getImageUrl]);

  // Se non ci sono allegati, non mostrare nulla
  if (!attachments || attachments.length === 0) return null;

  // Filtra le immagini dagli allegati per il lightbox
  const imageFiles = attachments.filter(file => isImageFile(file));
  
  return (
    <>
      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ color: colors?.primary || '#007bff', margin: 0 }}>{title}</h5>
          {downloadAllHandler && (
            <button 
              className="btn btn-sm" 
              onClick={downloadAllHandler}
              disabled={downloadInProgress}
              style={{ 
                backgroundColor: colors?.accent || '#17a2b8',
                color: colors?.textOnAccent || '#ffffff'
              }}
            >
              {downloadInProgress ? (
                <span><FontAwesomeIcon icon={faSpinner} spin /> Scaricando...</span>
              ) : (
                <span><FontAwesomeIcon icon={faFileZipper} /> Scarica tutti</span>
              )}
            </button>
          )}
        </div>
        <div className="d-flex flex-wrap gap-3">
          {attachments.map((file, index) => {
            const fileId = file._id || file.id || file.fileId || index;
            const isImage = isImageFile(file);
            
            // Gestione nome file per stringhe URL
            let fileName;
            if (typeof file === 'string') {
              // Estrai il nome file dall'URL
              const urlParts = file.split('/');
              fileName = urlParts[urlParts.length - 1].split('?')[0] || `File ${index + 1}`;
              // Decodifica caratteri URL se necessario
              fileName = decodeURIComponent(fileName);
            } else {
              // Prova prima con i campi nome, altrimenti estrai dall'URL
              fileName = file.fileName || file.filename || file.name;
              
              // Se non c'è nome ma c'è urlFile o url, estrai dall'URL
              if (!fileName && (file.urlFile || file.url)) {
                const urlParts = (file.urlFile || file.url).split('/');
                fileName = urlParts[urlParts.length - 1].split('?')[0];
                fileName = decodeURIComponent(fileName);
              }
              
              // Fallback finale
              if (!fileName) {
                fileName = `File ${index + 1}`;
              }
            }
            
            const fileIcon = getFileTypeIcon(file);
            const fileUrl = getImageUrl(file);
            
            return (
              <div 
                key={fileId} 
                className="border rounded p-3 position-relative" 
                style={{ 
                  width: '180px', 
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {/* Anteprima immagine o icona file */}
                <div className="text-center mb-2" style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isImage ? (
                    <>
                      <img 
                        src={fileUrl} 
                        alt={fileName}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100px', 
                          objectFit: 'contain'
                        }} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const fallbackIcon = e.target.parentNode.querySelector('.fallback-icon');
                          if (fallbackIcon) fallbackIcon.style.display = 'block';
                        }}
                      />
                      <FontAwesomeIcon 
                        className="fallback-icon"
                        icon={fileIcon} 
                        style={{ 
                          fontSize: '2.5rem', 
                          color: colors?.secondary || '#6c757d',
                          display: 'none'
                        }} 
                      />
                    </>
                  ) : (
                    <FontAwesomeIcon 
                      icon={fileIcon} 
                      style={{ 
                        fontSize: '2.5rem', 
                        color: colors?.secondary || '#6c757d'
                      }} 
                    />
                  )}
                </div>
                
                {/* Nome file */}
                <div 
                  className="text-center mb-2" 
                  style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500,
                    height: '40px', 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {fileName}
                </div>
                
                {/* Azioni */}
                <div className="d-flex justify-content-center gap-2">
                  {/* Download button */}
                  <button
                    className="btn btn-sm"
                    style={{ 
                      backgroundColor: colors?.accent || '#17a2b8',
                      color: colors?.textOnAccent || '#ffffff'
                    }}
                    onClick={() => handleDownload(file, index)}
                    disabled={downloadingFiles[typeof file === 'string' ? `url_${index}` : fileId]}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                  
                  {/* Preview button solo per immagini */}
                  {isImage && showPreview && (
                    <button
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: colors?.primary || '#007bff',
                        color: colors?.textOnPrimary || '#ffffff'
                      }}
                      onClick={() => {
                        // Trova l'indice del file corrente nella lista di sole immagini
                        const imageIndex = imageFiles.findIndex((img, imgIdx) => {
                          // Confronto robusto: prima prova con ID, poi con indice originale
                          const imgId = img._id || img.id || img.fileId;
                          const currentFileId = file._id || file.id || file.fileId;
                          
                          // Se entrambi hanno un ID, confronta gli ID
                          if (imgId && currentFileId) {
                            return imgId === currentFileId;
                          }
                          
                          // Altrimenti confronta gli URL o i file stessi
                          const imgUrl = getImageUrl(img);
                          const currentUrl = getImageUrl(file);
                          return imgUrl === currentUrl;
                        });
                        
                        openLightbox(attachments, Math.max(0, imageIndex));
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Lightbox component */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={currentImageIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

AttachmentsViewer.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.string,
      fileId: PropTypes.string,
      fileName: PropTypes.string,
      filename: PropTypes.string,
      name: PropTypes.string,
      path: PropTypes.string,
      url: PropTypes.string,
      fileUrl: PropTypes.string
    })
  ).isRequired,
  title: PropTypes.string,
  showPreview: PropTypes.bool,
  downloadAllHandler: PropTypes.func,
  downloadInProgress: PropTypes.bool
};

export default AttachmentsViewer;
